import { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { FiChevronDown, FiChevronRight, FiX, FiArrowRight } from "react-icons/fi";
import { LuHistory } from "react-icons/lu";

import { auditLogViewActions } from "../../store/auditLogViewSlice";
import { useGetAuditLogByIdQuery } from "../../store/services/auditLogServices";

const customStyles = {
    content: {
        position: "static",
        borderRadius: "16px",
        padding: 0,
        border: "none",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        maxWidth: "92vw",
    },
    overlay: {
        background: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
    },
};

Modal.setAppElement(document.getElementById("root"));

const ACTION_STYLES = {
    CREATE: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    UPDATE: "bg-secondary/10 text-secondary ring-secondary/30",
    "SOFT-DELETE": "bg-red-50 text-red-700 ring-red-200",
    DELETE: "bg-red-50 text-red-700 ring-red-200",
};

const HIDDEN_KEYS = new Set(["password"]);
const DATE_KEYS = new Set(["created_at", "updated_at", "birthdate"]);

const formatDate = (v) => {
    if (!v) return "—";
    try {
        return new Date(v).toLocaleString("en-GB", { timeZone: "Europe/Amsterdam" });
    } catch {
        return String(v);
    }
};

const labelize = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const safeStringify = (v) => {
    try {
        return JSON.stringify(v, null, 2);
    } catch {
        return String(v);
    }
};

const formatScalar = (key, value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (DATE_KEYS.has(key)) return formatDate(value);
    if (typeof value === "object") return safeStringify(value);
    return String(value);
};

const isHtmlBody = (key, tableName) =>
    key === "body" && (tableName || "").includes("notifications");

const tryParseTiers = (tiers) => {
    if (Array.isArray(tiers)) return tiers;
    if (typeof tiers === "string") {
        try {
            const parsed = JSON.parse(tiers);
            return Array.isArray(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }
    return null;
};

const Tiers = ({ tiers }) => {
    const parsed = tryParseTiers(tiers);
    if (!parsed) {
        return (
            <pre className="text-[12px] font-mono text-gray-700 whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                {typeof tiers === "object" ? safeStringify(tiers) : String(tiers ?? "—")}
            </pre>
        );
    }
    if (parsed.length === 0) {
        return <span className="italic text-gray-400 text-sm">Asnjë</span>;
    }
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {parsed.map((tier, i) => (
                <div key={i} className="rounded-lg border border-gray-200 bg-white p-2.5">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-secondary mb-1.5">
                        Niveli {i + 1}
                    </div>
                    <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                        {Object.entries(tier).map(([k, v]) => (
                            <div key={k} className="contents">
                                <dt className="text-gray-500 truncate">{labelize(k)}</dt>
                                <dd className="font-semibold text-gray-900 text-right truncate">
                                    {v === null || v === undefined || v === "" ? "—" : String(v)}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            ))}
        </div>
    );
};

const FieldValue = ({ keyName, value, tableName, tone = "neutral" }) => {
    const toneClass =
        tone === "old"
            ? "text-red-700 line-through decoration-red-300 decoration-1"
            : tone === "new"
            ? "text-emerald-700 font-semibold"
            : "text-gray-800";

    if (keyName === "tiers") return <Tiers tiers={value} />;
    if (isHtmlBody(keyName, tableName)) {
        return (
            <div
                className={`text-sm leading-relaxed ${toneClass} break-words`}
                dangerouslySetInnerHTML={{ __html: value || "" }}
            />
        );
    }
    if (typeof value === "object" && value !== null) {
        return (
            <pre className={`text-[12px] font-mono ${toneClass} whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-2.5 border border-gray-100`}>
                {safeStringify(value)}
            </pre>
        );
    }
    return <span className={`text-sm ${toneClass} break-words`}>{formatScalar(keyName, value)}</span>;
};

const ChangeRow = ({ keyName, oldValue, newValue, tableName }) => {
    const isStructured = keyName === "tiers" || isHtmlBody(keyName, tableName) || (typeof newValue === "object" && newValue !== null) || (typeof oldValue === "object" && oldValue !== null);
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-3.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
                {labelize(keyName)}
            </div>
            {isStructured ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-red-500 mb-1">Para</div>
                        <FieldValue keyName={keyName} value={oldValue} tableName={tableName} tone="old" />
                    </div>
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 mb-1">Pas</div>
                        <FieldValue keyName={keyName} value={newValue} tableName={tableName} tone="new" />
                    </div>
                </div>
            ) : (
                <div className="flex items-start gap-2 flex-wrap text-sm">
                    <FieldValue keyName={keyName} value={oldValue} tableName={tableName} tone="old" />
                    <FiArrowRight size={14} className="text-gray-400 mt-1.5 shrink-0" />
                    <FieldValue keyName={keyName} value={newValue} tableName={tableName} tone="new" />
                </div>
            )}
        </div>
    );
};

const FieldList = ({ entries, tableName }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {entries.map(([key, value]) => (
            <div key={key} className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    {labelize(key)}
                </div>
                <FieldValue keyName={key} value={value} tableName={tableName} tone="neutral" />
            </div>
        ))}
    </div>
);

const findChangedKeys = (oldData, newData) => {
    if (!oldData || !newData) return [];
    return Object.keys({ ...oldData, ...newData }).filter(
        (k) => !HIDDEN_KEYS.has(k) && !_.isEqual(oldData?.[k], newData?.[k])
    );
};

export default function AuditLogView() {
    const dispatch = useDispatch();
    const modalState = useSelector((state) => state.audit_log_view);

    const { data, isFetching } = useGetAuditLogByIdQuery({ id: modalState?.data?.id });
    const [showAllFields, setShowAllFields] = useState(false);

    useEffect(() => {
        setShowAllFields(false);
    }, [data?.id, modalState?.isOpen]);

    const closeModal = () => dispatch(auditLogViewActions.closeModal());

    const changedKeys = useMemo(
        () => findChangedKeys(data?.previous_data, data?.new_data),
        [data]
    );

    const newDataEntries = useMemo(
        () =>
            data?.new_data
                ? Object.entries(data.new_data).filter(([k]) => !HIDDEN_KEYS.has(k))
                : [],
        [data?.new_data]
    );

    const previousDataEntries = useMemo(
        () =>
            data?.previous_data
                ? Object.entries(data.previous_data).filter(([k]) => !HIDDEN_KEYS.has(k))
                : [],
        [data?.previous_data]
    );

    const tableName = data?.table_name;
    const actionType = data?.action_type;
    const actionPill = ACTION_STYLES[actionType] || "bg-gray-100 text-gray-700 ring-gray-200";

    return (
        <Modal
            isOpen={!!modalState?.isOpen}
            onRequestClose={closeModal}
            style={customStyles}
            contentLabel="Audit Log Details"
        >
            <div className="bg-white w-[820px] max-w-[92vw] max-h-[88vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-secondary text-white">
                            <LuHistory size={20} />
                        </span>
                        <div>
                            <div className="text-base font-semibold text-gray-900">Aktiviteti</div>
                            <div className="text-xs text-gray-500">Detaje të veprimit në sistem</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={closeModal}
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition"
                        aria-label="Mbyll"
                    >
                        <FiX size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 scrollbar-thin scrollbar-thumb scrollbar-track">
                    {/* Metadata strip */}
                    <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 flex flex-wrap items-center gap-x-4 gap-y-2">
                        <span className={`inline-flex items-center px-2.5 py-1 text-[11px] font-semibold rounded-md ring-1 ring-inset ${actionPill}`}>
                            {actionType || "—"}
                        </span>
                        {tableName && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                <span className="text-gray-400">Tabela</span>
                                <span className="font-semibold text-gray-800">{tableName}</span>
                            </div>
                        )}
                        {(data?.first_name || data?.last_name) && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                <span className="text-gray-400">nga</span>
                                <span className="font-semibold text-gray-800">
                                    {[data.first_name, data.last_name].filter(Boolean).join(" ")}
                                </span>
                            </div>
                        )}
                        <div className="ml-auto text-xs text-gray-500 tabular-nums">
                            {formatDate(data?.created_at)}
                        </div>
                    </div>

                    {tableName?.includes("receipts") && data?.vat_number && (
                        <div className="rounded-xl border border-gray-200/80 bg-gray-50/60 px-3.5 py-2 flex items-center gap-2 text-xs">
                            <span className="text-gray-500">VAT Number</span>
                            <span className="font-semibold text-gray-900 tabular-nums">{data.vat_number}</span>
                        </div>
                    )}

                    {/* Body content */}
                    {isFetching ? (
                        <div className="rounded-xl border border-gray-200/80 bg-white py-16 flex items-center justify-center text-sm text-gray-500">
                            Duke ngarkuar...
                        </div>
                    ) : data?.previous_data ? (
                        <>
                            {/* Changes */}
                            <section className="space-y-2">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                    {changedKeys.length > 0
                                        ? `Ndryshimet (${changedKeys.length})`
                                        : "Asnjë ndryshim"}
                                </h3>
                                {changedKeys.length === 0 ? (
                                    <div className="rounded-xl border border-gray-200/80 bg-white px-4 py-6 text-center text-sm text-gray-500">
                                        Të dhënat e mëparshme dhe të reja janë identike.
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {changedKeys.map((key) => (
                                            <ChangeRow
                                                key={key}
                                                keyName={key}
                                                oldValue={data.previous_data?.[key]}
                                                newValue={data.new_data?.[key]}
                                                tableName={tableName}
                                            />
                                        ))}
                                    </div>
                                )}
                            </section>

                            {/* Full snapshot toggle */}
                            <section>
                                <button
                                    type="button"
                                    onClick={() => setShowAllFields((s) => !s)}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 transition"
                                >
                                    {showAllFields ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                                    {showAllFields ? "Fshih të dhënat e plota" : "Shfaq të dhënat e plota"}
                                </button>
                                {showAllFields && (
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-red-500 mb-2">
                                                Para
                                            </h4>
                                            <FieldList entries={previousDataEntries} tableName={tableName} />
                                        </div>
                                        <div>
                                            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 mb-2">
                                                Pas
                                            </h4>
                                            <FieldList entries={newDataEntries} tableName={tableName} />
                                        </div>
                                    </div>
                                )}
                            </section>
                        </>
                    ) : data?.new_data ? (
                        <section className="space-y-2">
                            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                Të dhënat e krijuara
                            </h3>
                            <FieldList entries={newDataEntries} tableName={tableName} />
                        </section>
                    ) : (
                        <div className="rounded-xl border border-gray-200/80 bg-white py-16 flex items-center justify-center text-sm text-gray-500">
                            Nuk ka të dhëna për të shfaqur.
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 px-6 py-3 border-t border-gray-100 bg-gray-50/60">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                    >
                        Mbyll
                    </button>
                </div>
            </div>
        </Modal>
    );
}
